import { useEffect, useState } from 'react';
import { useNotify, useTranslate } from 'react-admin';

import {
    getDashboardV3DepositKpi,
    getDashboardV3Deposits,
} from '../services/dashboardV3.service';
import type {
    DashboardV3DepositKpiData,
    DashboardV3DepositKpiParams,
    DashboardV3DepositListParams,
    DashboardV3DepositRow,
} from '../services/dashboardV3.types';

const EMPTY_DEPOSIT_KPI: DashboardV3DepositKpiData = {
    depositUsers: 0,
    depositUsersDelta: null,
    depositTransactions: 0,
    depositTransactionsDelta: null,
    depositAmount: 0,
    depositAmountDelta: null,
};

export function useDashboardV3DepositData(
    kpiParams: DashboardV3DepositKpiParams,
    listParams: DashboardV3DepositListParams
) {
    const notify = useNotify();
    const t = useTranslate();
    const [kpi, setKpi] = useState<DashboardV3DepositKpiData>(EMPTY_DEPOSIT_KPI);
    const [list, setList] = useState<DashboardV3DepositRow[]>([]);
    const [total, setTotal] = useState(0);
    const [kpiLoading, setKpiLoading] = useState(false);
    const [listLoading, setListLoading] = useState(false);

    useEffect(() => {
        let active = true;
        setKpiLoading(true);

        getDashboardV3DepositKpi(kpiParams)
            .then((response) => {
                if (!active) return;
                setKpi(response?.data ?? EMPTY_DEPOSIT_KPI);
            })
            .catch((error: any) => {
                if (!active) return;
                setKpi(EMPTY_DEPOSIT_KPI);
                notify(
                    error?.message ||
                    t('module.dashboardV3.deposits.messages.kpiLoadFailed', {
                        _: 'Failed to load deposit KPI data',
                    }),
                    { type: 'warning' }
                );
            })
            .finally(() => {
                if (active) setKpiLoading(false);
            });

        return () => {
            active = false;
        };
    }, [kpiParams, notify, t]);

    useEffect(() => {
        let active = true;
        setListLoading(true);

        getDashboardV3Deposits(listParams)
            .then((response) => {
                if (!active) return;
                setList(response?.data?.list ?? []);
                setTotal(Number(response?.total ?? 0));
            })
            .catch((error: any) => {
                if (!active) return;
                setList([]);
                setTotal(0);
                notify(
                    error?.message ||
                    t('module.dashboardV3.deposits.messages.listLoadFailed', {
                        _: 'Failed to load deposit data',
                    }),
                    { type: 'warning' }
                );
            })
            .finally(() => {
                if (active) setListLoading(false);
            });

        return () => {
            active = false;
        };
    }, [listParams, notify, t]);

    return {
        kpi,
        list,
        total,
        kpiLoading,
        listLoading,
    };
}
